























import Vue, { PropType } from "vue";
import LoadingBlock from "@/components/loading/LoadingBlock.vue";
import MessageView, { MessageViewParam } from "@/components/MessageView.vue";
import { SolanProject } from "@/ts/objects/solan/value/SolanProject";
import { SolanStoreT } from "@/store/SolanStore";
import { messages } from "@/ts/const/Messages";
import { Err } from "@/ts/objects/Err";
import { NavigationGuardNext, Route } from "vue-router";
import { SolanRepository } from "@/ts/repositories/SolanRepository";
import { UserRepository } from "@/ts/repositories/UserRepository";
import { words, Words } from "@/ts/const/Words";
import { PageLeaveService } from "@/ts/services/PageLeaveService";
import { EditableSolanRubric } from "@/ts/objects/solan/editable/EditableSolanRubric";
import { emptySaveResult, flattenSaveResults, SaveResult } from "@/ts/objects/editable/SaveResult";
import log from "loglevel";
import { isNullish } from "@/ts/utils";
import SolanRubricsTable from "@/views/solan/components/SolanRubricsTable/SolanRubricsTable.vue";
import SolanCreateRubricButton from "@/views/solan/components/SolanCreateRubricButton/SolanCreateRubricButton.vue";

export default Vue.extend({
  name: "SolanStudentViewRubricsT",
  components: { SolanCreateRubricButton, SolanRubricsTable, LoadingBlock, MessageView },
  props: {
    solanStoreT: { type: Object as PropType<SolanStoreT>, required: true },
    userRepository: { type: Object as PropType<UserRepository>, required: true },
    solanRepository: { type: Object as PropType<SolanRepository>, required: true },
  },
  created() {
    const vm = this;
    this.periodicSaverId = window.setInterval(() => vm.saveAll(false), 15000);
    this.pageLeaveService = new PageLeaveService({
      onLeaveStart: async () => {
        await this.saveAll(true);
      },
      requirementToLeave: async () => !this.needSave,
    });
    this.reloadData();
  },
  beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext) {
    this.pageLeaveService!.tryLeave().then(ok => {
      if (!ok) {
        next(false);
        return;
      }
      this.reloadData();
      next();
    });
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
    this.pageLeaveService!.tryLeave().then(ok => {
      if (!ok) {
        next(false);
        return;
      }
      next();
    });
  },
  beforeDestroy() {
    clearInterval(this.periodicSaverId);
  },
  data(): {
    messageView: MessageViewParam | null;

    periodicSaverId: number | undefined;
    pageLeaveService: PageLeaveService | null;

    project: SolanProject | null;
    rubrics: EditableSolanRubric[] | null;

    selectedProcess: number;
    addRubricEnabled: boolean;

    words: Words;
  } {
    return {
      messageView: null,

      periodicSaverId: undefined,
      pageLeaveService: null,

      project: null,
      rubrics: null,

      selectedProcess: 0,
      addRubricEnabled: true,

      words,
    };
  },
  computed: {
    needSave(): boolean {
      const rubrics = this.rubrics;
      if (rubrics === null) return false;
      return rubrics.some(s => s.needSave());
    },
    reversedRubrics(): EditableSolanRubric[] | null {
      const rubrics = this.rubrics;
      if (rubrics === null) return null;
      return rubrics.slice().reverse();
    },
  },
  methods: {
    async reloadData() {
      this.rubrics = null;
      this.messageView = null;

      const studentUserId = this.$route.params["solanStudentUserId"];
      if (isNullish(studentUserId)) {
        this.messageView = { message: messages.pleaseSelectStudent };
        return;
      }

      await this.solanStoreT.reloadProjectsIfStudentChanged({
        userRepository: this.userRepository,
        solanRepository: this.solanRepository,
        studentUserId,
      });

      const projectId = this.$route.params["solanProjectId"];
      if (isNullish(projectId)) {
        this.messageView = { message: messages.pleaseSelectSolanTheme };
        return;
      }

      const project = await this.solanStoreT.getProject(projectId).getDataWithTimeout();
      if (isNullish(project)) {
        this.messageView = { message: messages.pleaseSelectSolanTheme };
        return;
      }

      const resp = await this.solanRepository.listEditableSolanRubrics(project.projectId, true);
      if (resp instanceof Err) {
        this.messageView = { message: messages.failedToLoadData, fadeIn: true };
        return;
      }

      this.project = project;
      this.rubrics = resp;
    },
    async saveAll(force: boolean): Promise<SaveResult> {
      const rubrics = this.rubrics;
      if (rubrics === null) return emptySaveResult();
      log.debug("SAVING!");
      return flattenSaveResults(await Promise.all(rubrics.map(r => r.saveAllChanges(force))));
    },
    selectProcess(process: number) {
      this.selectedProcess = process;
    },
    async addRubric(process: number) {
      const project = this.project;
      const rubrics = this.rubrics;
      if (isNullish(project) || isNullish(rubrics)) return;

      // 教職員は児童生徒入力値ロック済でも、変更できる・・・とする。
      // if (project.studentInputLocked) return;

      const projectId = project?.projectId;

      if (!this.addRubricEnabled) return;
      this.addRubricEnabled = false;
      const resp = await this.solanRepository.postAndGetEditableSolanRubric(projectId, process, true);
      this.addRubricEnabled = true;
      if (resp instanceof Err) return; // TODO エラー処理？
      rubrics.push(resp);
    },
    async deleteRubric(rubricId: string) {
      const deletingRubric = this.rubrics?.find(r => r.rubricId === rubricId);
      if (isNullish(deletingRubric)) return;

      // 入力したものがすべて消えます。消しますか？
      if (!window.confirm(messages.confirmDeleteAllYourInputs)) {
        return;
      }

      const resp = await this.solanRepository.deleteRubric(deletingRubric.projectId, deletingRubric.rubricId);
      if (resp instanceof Err) {
        log.error("Failed to delete rubric.");
        return;
      }
      this.rubrics = this.rubrics?.filter(r => r !== deletingRubric) ?? null;
    },
  },
});
